import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, Facebook, Menu, X, ChevronDown, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface HeaderProps {
  scrolled: boolean;
}

const Header: React.FC<HeaderProps> = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Immobili', path: '/immobili' },
    { label: 'Vendi con Noi', path: '/vendi-con-noi' },
    { label: 'Chi Siamo', path: '/chi-siamo' },
    { label: 'Contatti', path: '/contatti' }
  ];

  return (
    <header className={`bg-white fixed w-full z-50 transition-shadow duration-300 ${scrolled ? 'shadow-lg' : 'shadow-sm'}`}>
      <div className="relative z-10">
        {/* Top Bar */}
        <div className="bg-primary text-white text-sm py-2 hidden md:block">
          <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-2 text-secondary" />
                <span>Via Pisana 327, Scandicci 50018</span>
              </div>
              <div className="flex items-center">
                <Phone className="h-4 w-4 mr-2 text-secondary" />
                <span>+39 327 8855115</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <a href="https://www.instagram.com/molinaro_immobiliare/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">
                <Instagram className="h-4 w-4" />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61571288444118" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">
                <Facebook className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
        
        {/* Main Navigation */}
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex-shrink-0">
              <img src="/logo_molinaro.png" alt="Molinaro Immobiliare" className="h-16 md:h-20" />
            </Link>
            
            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map((item, index) => (
                <div key={index} className="relative group">
                  <Link 
                    to={item.path} 
                    className={`px-4 py-2 font-semibold uppercase text-gray-700 hover:text-primary transition-colors relative group ${location.pathname === item.path ? 'text-primary' : ''}`}
                  >
                    {item.label}
                    <span className={`absolute bottom-0 left-0 w-0 h-0.5 bg-secondary transition-all duration-300 group-hover:w-full ${location.pathname === item.path ? 'w-full' : ''}`}></span>
                  </Link>
                </div>
              ))}
            </nav>
            
            {/* Mobile menu button */}
            <div className="flex md:hidden">
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-700 hover:text-primary"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-white shadow-lg overflow-hidden"
            >
              <div className="px-4 py-2 space-y-1">
                {navItems.map((item, index) => (
                  <div key={index}>
                    <Link 
                      to={item.path} 
                      className={`block py-3 font-semibold text-gray-700 hover:text-primary border-b border-gray-100 ${location.pathname === item.path ? 'text-primary' : ''}`}
                    >
                      {item.label}
                    </Link>
                  </div>
                ))}
                
                <div className="py-3 space-y-3">
                  <div className="flex items-center text-gray-600">
                    <MapPin className="h-5 w-5 mr-3 text-primary" />
                    Via Pisana 327, Scandicci
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Phone className="h-5 w-5 mr-3 text-primary" />
                    +39 327 8855115
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Mail className="h-5 w-5 mr-3 text-primary" />
                    info@molinaroimmobiliare.it
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;